import React, { FC, useState } from "react";
import { Tooltip, message } from "antd";
import { DownloadOutlined, CheckOutlined } from "@ant-design/icons";
import moment from "moment";
import { BeamFile } from "./types";
import BeamIcon, { fileIconSrc } from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * Files / documents message type — a horizontal document card so a stack of
 * them scans as a list rather than a grid of tiles.
 *
 * The thumbnail is the app's own per-extension artwork from
 * public/images/files-icon, so a PDF here looks like a PDF everywhere else in
 * Beam. Size sits beside the name rather than in a coloured tile.
 */

interface FilesCardProps {
  files: BeamFile[];
  onPreview?: (file: BeamFile) => void;
  onDownload?: (file: BeamFile) => void;
}

const FileRow: FC<{
  file: BeamFile;
  onPreview?: (file: BeamFile) => void;
  onDownload?: (file: BeamFile) => void;
}> = ({ file, onPreview, onDownload }) => {
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    // clipboard API is https-only; the textarea path keeps this working on
    // plain-http dev hosts too.
    const done = () => {
      setCopied(true);
      message.success("Link copied");
      window.setTimeout(() => setCopied(false), 1600);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(file.url).then(done);
      return;
    }
    const field = document.createElement("textarea");
    field.value = file.url;
    document.body.appendChild(field);
    field.select();
    document.execCommand("copy");
    document.body.removeChild(field);
    done();
  };

  return (
    <article className="mt-file">
      <img
        className="mt-file__badge"
        src={fileIconSrc(file.name)}
        alt={file.extension}
      />

      <div className="mt-file__meta">
        <h5 className="mt-file__name" title={file.name}>
          {file.name}
        </h5>
        {/* Built as a list so a missing size or version drops its separator
            with it, instead of leaving a stray dot behind. */}
        <p className="mt-file__sub">
          {[
            file.sizeLabel && (
              <span key="size" className="mt-file__size">
                {file.sizeLabel}
              </span>
            ),
            file.version && (
              <span key="version" className="mt-file__version">
                {file.version}
              </span>
            ),
            file.uploader,
            moment(file.uploadedAt).format("D MMM YYYY, h:mm A"),
          ]
            .filter(Boolean)
            .map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="mt-file__sep">·</span>}
                {part}
              </React.Fragment>
            ))}
        </p>
      </div>

      <div className="mt-file__actions">
        <Tooltip title="Preview inline">
          <button type="button" onClick={() => onPreview?.(file)}>
            <BeamIcon name="expand" size={15} />
          </button>
        </Tooltip>
        {/* No download or link glyph exists in the app set, so these two stay
            on antd rather than substituting unrelated artwork. */}
        <Tooltip title="Download">
          <button type="button" onClick={() => onDownload?.(file)}>
            <DownloadOutlined />
          </button>
        </Tooltip>
        <Tooltip title={copied ? "Copied" : "Copy link"}>
          <button
            type="button"
            className={copied ? "is-done" : ""}
            onClick={copyLink}
          >
            {copied ? <CheckOutlined /> : <BeamIcon name="doc" size={15} />}
          </button>
        </Tooltip>
      </div>
    </article>
  );
};

const FilesCard: FC<FilesCardProps> = ({ files, onPreview, onDownload }) => (
  <section className="mt-card mt-files">
    <header className="mt-card__head">
      <h4>Files</h4>
      <span className="mt-card__count">{files.length}</span>
    </header>
    <div className="mt-card__body mt-card__body--flush">
      {files.map((file) => (
        <FileRow
          key={file.id}
          file={file}
          onPreview={onPreview}
          onDownload={onDownload}
        />
      ))}
    </div>
  </section>
);

export default FilesCard;
